import axios, { DefaultResponse, TypedResponse } from "../general/HttpClient";
import { IdentityUser, Organization } from "../users/UserStore";

export interface OrgChartNode {
    id: string;
    name: string;
    groupID: string;
    groupName: string;
    groupTypeName: string;
    children: OrgChartNode[];
}

export function GetGroups(organizationId, groupTypes = "", groupFilter = ""): Promise<Group[]> {
    return axios
        .get(`/api/groups/${organizationId}`, {
            params: { groupTypes, groupFilter },
        })
        .then((res) => res.data);
}

export function GetOrgChart(organizationId, groupTypes = ""): Promise<OrgChartNode[]> {
    return axios
        .get(`/api/groups/${organizationId}/orgchart`, {
            params: { groupTypes },
        })
        .then((res) => res.data);
}

export function GetGroup(organizationId, groupId): Promise<Group> {
    return axios.get(`/api/groups/${organizationId}/groups/${groupId}`).then((res) => res.data);
}

export function GetGroupCustomTypes(organizationId): Promise<GroupCustomType[]> {
    return axios.get(`/api/groups/${organizationId}/groupTypes`).then((res) => res.data);
}

export function GetOrganizationMembers(organizationId, groupId): Promise<GetUsersResponse> {
    return axios
        .get(`/api/groups/${organizationId}/allmembers`, {
            params: {
                query: "",
                filter: groupId,
                skip: 0,
                limit: 0,
                delegated: false,
                allowStaff: true,
                allowStudents: true,
                allowParents: true,
                allowGuests: true,
            },
        })
        .then((res) => res.data);
}

export function CreateGroup(organizationId, data: CreateGroupRequest): Promise<TypedResponse<Group>> {
    return axios.post(`/api/groups/${organizationId}`, data).then((res) => res.data);
}

export function AddMember(organizationId, groupId, data: CreateGroupMemberRequest): Promise<DefaultResponse> {
    return axios.post(`/api/groups/${organizationId}/${groupId}/members`, data).then((res) => res.data);
}

export function RemoveMember(organizationId, groupId, userId): Promise<DefaultResponse> {
    return axios.delete(`/api/groups/${organizationId}/${groupId}/members/${userId}`).then((res) => res.data);
}

export function CreateGroupType(organizationId, data: CreateGroupTypeRequest): Promise<TypedResponse<Group>> {
    return axios.post(`/api/groups/${organizationId}/groupTypes`, data).then((res) => res.data);
}

export function DeleteGroup(id): Promise<TypedResponse<Group>> {
    return axios.delete(`/api/groups/${id}`).then((res) => res.data);
}

export function AddChild(organizationId, groupId, data: CreateChildGroupRequest): Promise<DefaultResponse> {
    return axios.post(`/api/groups/${organizationId}/${groupId}/children`, data).then((res) => res.data);
}

export function RemoveChild(organizationId, groupId, childId): Promise<DefaultResponse> {
    return axios.delete(`/api/groups/${organizationId}/${groupId}/children/${childId}`).then((res) => res.data);
}

export interface CreateGroupRequest {
    name: string;
    typeID: string;
}

export interface CreateGroupTypeRequest {
    name: string;
}

export interface CreateGroupMemberRequest {
    userID: string;
    type: GroupMembershipType;
}

export interface CreateChildGroupRequest {
    groupID: string;
}

export interface Group {
    id: string;
    name: string;
    type: GroupType;
    organizationID: string;
    organization: Organization;
    source: string;
    sourceID: string;
    groupCustomTypeID: string;
    groupCustomType: GroupCustomType;
    data: GroupData;
    groupUsers: GroupUserMembership[];
    users: IdentityUser[];
    children: GroupGroupMembership[];
    childGroups: Group[];
    parents: GroupGroupMembership[];
    parentGroups: Group[];
    groupTags: GroupTagMembership[];
    tags: GroupTag[];
    allUsers: GroupUserMembership[];
}

export interface GroupData {
    description: string;
}

export enum GroupType {
    Default = 0,
    Custom = 1,
    Roster = 3,
    Class = 4,
    Dynamic = 5,
}

export const GroupTypeMap = {
    0: "Default",
    1: "Custom",
    3: "Roster",
    4: "Class",
};

export interface GroupCustomType {
    id: string;
    name: string;
    organizationID: string;
    organization: Organization;
    source: string;
    sourceID: string;
}

export interface GroupTag {
    id: string;
    name: string;
    organizationID: string;
    organization: Organization;
    groups: Group[];
}

export interface GroupUserMembership {
    groupID: string;
    group: Group;
    memberID: string;
    member: IdentityUser;
    membershipType: GroupMembershipType;
    source: string;
}

export enum GroupMembershipType {
    Member = 0,
    Owner = 1,
}

export interface GroupGroupMembership {
    groupID: string;
    group: Group;
    memberID: string;
    member: Group;
}

export interface GroupTagMembership {
    groupID: string;
    group: Group;
    tagId: string;
    tag: GroupTag;
}

export interface GetUsersResponse {
    members: IdentityUser[];
    total: number;
    success: boolean;
    message: string;
}
